import { useCallback, useEffect, useRef, useState } from "react";
import { getDataClient } from "@/lib/dataClient";

export interface QueryState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Genel dataClient veri çekme deseni. Veri, hata ve yeniden yükleme durumunu yönetir.
 *
 * Yarış durumu (race condition) koruması içerir: bağımlılıklar (deps) değiştiğinde
 * eski isteğin cevabı, yeni isteğin cevabından sonra dönerse bile yok sayılır.
 * Böylece örn. önce filtresiz (tüm dersler) sonra filtreli (yalnızca 5. sınıf)
 * çalışan iki istekte, yavaş dönen filtresiz cevap doğru cevabın üzerine yazamaz.
 */
export function useDataQuery<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher();
      if (id === requestId.current) setData(result);
    } catch (e) {
      if (id === requestId.current) {
        setError(e instanceof Error ? e.message : "Veriler yüklenirken bir hata oluştu.");
      }
    } finally {
      if (id === requestId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}

/**
 * Mevcut oturumdaki kullanıcının id'sini döndürür (yoksa null).
 */
export async function getCurrentUserId(): Promise<string | null> {
  const { data } = await getDataClient().auth.getUser();
  return data.user?.id ?? null;
}